import { Delete } from '@mui/icons-material';
import { Box, Button, Chip, Grid, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { EEntityType, EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import {
    ESuggestionRequestType,
    ESuggestionResponseType,
    type ISuggestionRejection,
    type TSuggestionsRequest,
    type TSuggestionsResponse,
} from '@sharedInterfaces/ISuggestion';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import RowElement from '../../General/FormularElements/RowElement/RowElement';
import LoadingBox from '../../General/LoadingBox/LoadingBox';

interface RejectionsDialogProps {
    id: string;
    resolve: () => void;
}

const rejectionTopic = EWebsocketTopics.SUGGESTIONS;

const RejectionsDialog: React.FC<RejectionsDialogProps> = (props) => {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const [tabIndex, setTabIndex] = React.useState<number>(0);
    const [skills, setSkills] = React.useState<ISuggestionRejection[] | null>(null);
    const [certificates, setCertificates] = React.useState<ISuggestionRejection[] | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);

    const selectedEntityType = tabIndex === 0 ? EEntityType.SKILL : EEntityType.CERTIFICATE;

    const elements = selectedEntityType === EEntityType.SKILL ? skills : certificates;

    React.useEffect(() => {
        const request: TSuggestionsRequest = { type: ESuggestionRequestType.GET_REJECTIONS };
        WebSocketClient.sendMessage(rejectionTopic, request);
    }, []);

    React.useEffect(() => {
        const subKey = WebSocketClient.subscribe<TSuggestionsResponse>(rejectionTopic, (data: TSuggestionsResponse) => {
            switch (data.type) {
                case ESuggestionResponseType.REJECTIONS:
                    setSkills(data.rejections[EEntityType.SKILL]);
                    setCertificates(data.rejections[EEntityType.CERTIFICATE]);
                    setLoading(false);
                    break;
            }
        });
        return () => {
            WebSocketClient.unsubscribe(subKey);
        };
    }, []);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    const onDelete = (newTitle: string) => {
        const deleteEvent: TSuggestionsRequest = {
            type: ESuggestionRequestType.DELETE_REJECTION,
            newTitle,
            entityType: EEntityType.SKILL,
        };
        WebSocketClient.sendMessage(rejectionTopic, deleteEvent);

        if (selectedEntityType === EEntityType.SKILL) {
            setSkills((prev) => (prev ? prev.filter((s) => s.newTitle !== newTitle) : null));
        } else if (selectedEntityType === EEntityType.CERTIFICATE) {
            setCertificates((prev) => (prev ? prev.filter((c) => c.newTitle !== newTitle) : null));
        }
    };

    return (
        <Dialog
            id={props.id}
            title={langStrings.rejectionsDialogTitle}
            onClose={props.resolve}
            footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Button variant="contained" color="primary" onClick={props.resolve}>
                        {langStrings.oftenUsed.close}
                    </Button>
                </div>
            }
        >
            <Box sx={{ width: '100%' }}>
                <Tabs value={tabIndex} onChange={handleTabChange} aria-label="Rejection Tabs" variant="fullWidth">
                    <Tab label={langStrings.oftenUsed.skills} />
                    <Tab label={langStrings.oftenUsed.certificates} />
                </Tabs>
                {loading ? (
                    <LoadingBox />
                ) : (
                    <Box sx={{ padding: 2 }}>
                        {!elements?.length && (
                            <Typography variant="body2" color="text.secondary" sx={{ padding: 2 }}>
                                {langStrings.noRejectionsAvailable}
                            </Typography>
                        )}
                        <Grid container spacing={2}>
                            {elements?.map((rejection) => (
                                <Grid size={{ xs: 12 }} key={`${selectedEntityType}-${rejection.newTitle}`}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            padding: 2,
                                        }}
                                    >
                                        <Box>
                                            <RowElement title={langStrings.title}>
                                                <Typography variant="body1">{rejection.newTitle}</Typography>
                                            </RowElement>
                                            <RowElement title={langStrings.declineSuggestionReasonLabel}>
                                                {rejection.declinedReason}
                                            </RowElement>
                                            <RowElement title={langStrings.oftenUsed.otherNames}>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 1 }}>
                                                    {rejection.titles?.length ? (
                                                        rejection.titles.map((title, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={title}
                                                                variant="outlined"
                                                                color="secondary"
                                                            />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {langStrings.notSet}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </RowElement>
                                        </Box>
                                        <IconButton onClick={() => onDelete(rejection.newTitle)}>
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default RejectionsDialog;
